import { Injectable } from '@angular/core'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import {
  Channel,
  AddPPIDChannel,
  AddPPIDChannelComplete,
  AddPPIDChannelFailed,
  selectSomeOnce,
  loadRentals,
  loadRentalsImages,
  selectAllRentals,
  addRentalImagesComplete,
} from '@tokeet-frontend/tv3-platform'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { LoadConnectionsByChannel, RefreshConnectionsForChannel } from '@tv3/store/connection/connection.actions'
import * as lodash from 'lodash'
import { timer } from 'rxjs'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'

// Machine-readable reason for a failed channel connect, from the backend's error_code when
// present (TV4-5902), falling back to the HTTP status for older backends.
export function connectChannelErrorReason(error: any): string {
  const code = error?.error?.error_code
  if (code) return code
  const status = error?.status
  if (status === 409) return 'property_already_connected'
  // pre-TV4-5902 backends report duplicates as a plain 400 with this message
  if (status === 400 && /already connected/i.test(error?.error?.error || '')) return 'property_already_connected'
  if (status >= 500) return 'channel_unavailable'
  return 'unknown'
}

// How long to give the queued listing import before re-reading rentals.
const rentalRefreshDelay = 3000

@Injectable()
export class ChannelConnectHelperService {
  constructor(private store: Store<any>, private actions: Actions, private amplitudeService: AmplitudeService) {}

  connectChannelWithPropertyId(channel: Channel, propertyId: any) {
    this.store.dispatch(
      AddPPIDChannel({
        channelId: channel.id,
        data: {
          property_id: propertyId,
          channel_id: channel.id,
          channel_name: channel.name,
          subscriber: channel.friendlyName,
          handler: channel.handler,
        },
        channelName: channel.name,
      })
    )
    return this.actions.pipe(
      ofType(AddPPIDChannelComplete, AddPPIDChannelFailed),
      filter((action) => action.channelName === channel.name),
      take(1),
      map((action) => {
        if (action.type === AddPPIDChannelFailed.type) {
          const error = (action as ReturnType<typeof AddPPIDChannelFailed>).error
          this.amplitudeService.logEvent(
            `add-channel-${channel.name?.toLowerCase()}-failed-${connectChannelErrorReason(error)}`
          )
          // this failure is already surfaced to the user (the AddPPIDChannel effect toasts
          // ActionFailed for non-booking channels; the BDC wizard shows an inline alert), so
          // downstream catch-all handlers check the flag to avoid reporting it a second time
          if (error && typeof error === 'object') {
            error.reportedViaActionFailed = true
          }
          throw error
        }
        this.doAfterConnected(channel)
        return action
      })
    )
  }

  doAfterConnected(channel: Channel) {
    this.amplitudeService.logEvent('add-channel')
    const channelName = channel.name?.toLowerCase()
    this.amplitudeService.logEvent(`add-channel-${channelName}`)
    this.refreshRooms(channel)
  }

  // Schedules a backend room refresh and reloads connections. No analytics -- safe to call
  // repeatedly (the step-4 rooms Retry uses it), unlike doAfterConnected which logs the
  // add-channel Amplitude events and must fire once per actual connect.
  refreshRooms(channel: Channel) {
    this.store.dispatch(LoadConnectionsByChannel({ channelName: channel.name, channelId: channel.id }))
    this.store.dispatch(
      RefreshConnectionsForChannel({
        channelName: channel.name,
        channelId: channel.id,
        silent: true,
      })
    )
    // Connecting can create rentals server-side (Airbnb Quick Connect queues a listing import),
    // and the fresh connections reference them by id only -- the API never returns rental_name
    // for API connections. Without this the Linked Rental column renders '....' until a reload.
    //
    // The import is queued to the listing service rather than run inline, so there is nothing to
    // wait on: RefreshConnectionsForChannelComplete only means the refresh was *scheduled*.
    // Reloading immediately would race the import and read the rentals before it created them,
    // so give it a moment. The wizard still shows its completion step at this point, which is
    // where the user spends the delay.
    timer(rentalRefreshDelay).subscribe(() => this.store.dispatch(loadRentals({})))
  }

  rentalImageCountsGuard() {
    // Refresh image counts when images are added
    return this.actions.pipe(
      ofType(addRentalImagesComplete),
      map((action) => lodash.compact(lodash.uniq(lodash.map(action.images, (image) => image.rentalId)))),
      tap((rentalIds) => this.store.dispatch(loadRentalsImages({ ids: rentalIds })))
    )
  }
}
